import React, { useCallback, useEffect, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { Button, LinkButton } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { Text } from '@/atoms/Text'
import { useShopifyCart } from '@/services/Shopify/hooks'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useShopifyUserAttributes } from '@/views/PreSales/hooks/useShopifyUserAttributes'
import { GroupTicketsModalTypes } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/GroupTicketsModal'
import { TicketOptionDiscount } from './TicketOptionDiscount'
import { calculateDiscountPercentage, getDiscountCode } from './experiment/discountCodeUtils'

const OPTIONS = [10, 20, 50, 100, 200]

interface DiscountRate {
  quantity: number
  discountRate: number
}

interface DiscountLabel {
  quantity: number
  label: string
}

interface Props {
  setModalType: React.Dispatch<React.SetStateAction<GroupTicketsModalTypes>>
  ticketPrice: number
  theatricalSlug: string
  groupTicketProductVariantId: string
  discountRates: DiscountRate[]
  discountLabels: DiscountLabel[]
  variantNumber: 1 | 2 | 3
}

export const ShopifyGroupTicketsDiscountVariation: React.FC<Props> = ({
  setModalType,
  ticketPrice,
  groupTicketProductVariantId,
  theatricalSlug,
  discountRates,
  discountLabels,
  variantNumber,
}) => {
  const { t } = useTranslate('tickets')
  const track = useSafeTrack()
  const { query } = useSafeRouter()
  const { trackProductAdded, trackProductViewed } = useEcommerceEvents()
  const [selectedQuantity, setSelectedQuantity] = useState(OPTIONS[3])
  const [isProcessing, setIsProcessing] = useState(false)
  const { createCart, applyDiscountCode } = useShopifyCart()
  const [shopifyCheckoutUrl, setShopifyCheckoutUrl] = useState('')

  const attributes = useShopifyUserAttributes('theatrical-group-tickets', 'theatrical group tickets', theatricalSlug)

  // Find the discount rate for selected quantity
  const getDiscountRate = useCallback(
    (quantity: number): number => {
      const found = discountRates.find((rate) => rate.quantity === quantity)
      return found ? found.discountRate : 0.5 // Fallback to 50% if not found
    },
    [discountRates],
  )

  // Find the discount label for selected quantity
  const getDiscountLabel = (quantity: number): string => {
    const found = discountLabels.find((label) => label.quantity === quantity)
    return found ? found.label : '50% OFF' // Fallback to 50% OFF if not found
  }

  // this will get us the actual code we need to pass to shopify for the discount
  const getDiscountCodeForQuantity = useCallback(
    (quantity: number): string => {
      const discountRate = getDiscountRate(quantity)
      return getDiscountCode(quantity, variantNumber, discountRate)
    },
    [getDiscountRate, variantNumber],
  )

  const getMaxDiscountPercentage = (): number => {
    return Math.max(...discountRates.map((rate) => calculateDiscountPercentage(rate.discountRate)))
  }

  useEffect(() => {
    if (!selectedQuantity) return

    setIsProcessing(true)
    const createShopifyCheckoutUrl = async () => {
      try {
        const { cart } = await createCart({
          lines: [
            {
              quantity: selectedQuantity,
              merchandiseId: `gid://shopify/ProductVariant/${groupTicketProductVariantId}`,
            },
          ],
          attributes,
        })

        if (!cart?.id) return null

        const discountCodeParam = getItemOrFirstEntry(query?.groupDiscountCode)
        const discountCode = discountCodeParam ? discountCodeParam : getDiscountCodeForQuantity(selectedQuantity)

        const finalCart = await applyDiscountCode({ cartId: cart.id, discountCodes: [discountCode] })

        if (!finalCart?.cart?.checkoutUrl) return

        setShopifyCheckoutUrl(finalCart?.cart?.checkoutUrl)
      } catch (err) {
        logger().error('Failed to create shopifyCheckoutUrl for group tickets', err)
      } finally {
        setIsProcessing(false)
      }
    }

    createShopifyCheckoutUrl()
  }, [
    applyDiscountCode,
    attributes,
    createCart,
    groupTicketProductVariantId,
    query?.groupDiscountCode,
    selectedQuantity,
    getDiscountCodeForQuantity,
  ])

  useEffect(() => {
    track('Group Tickets Options Viewed')
  }, [track])

  const handleCtaClick = async (quantity: number) => {
    try {
      const discountRate = getDiscountRate(quantity)
      const discountPercent = calculateDiscountPercentage(discountRate)
      const discountCode = getDiscountCodeForQuantity(quantity)
      // Use a known valid analytics event
      track('Group Tickets Checkout Started', {
        quantity,
        discountPercentage: discountPercent,
        discountCode,
        variantNumber,
      })

      trackProductAdded({
        category: 'group tickets',
        funnel: 'theatrical',
        name: `Group Ticket ${theatricalSlug}`,
        price: Number((ticketPrice * discountRate).toFixed(2)),
        currency: 'USD',
        quantity: quantity,
        product_id: `group-tickets-${theatricalSlug}`,
        sku: `group-tickets-${theatricalSlug}`,
        projectSlug: theatricalSlug,
      })
    } catch (err) {
      logger().error('Failed to create group ticket checkout', err)
    }
  }

  const maxDiscountPercentage = getMaxDiscountPercentage()

  return (
    <InView
      triggerOnce
      onChange={(inView) => {
        if (!inView) return

        const discountRate = getDiscountRate(selectedQuantity)

        trackProductViewed({
          category: 'group tickets',
          funnel: 'theatrical',
          name: `Group Ticket ${theatricalSlug}`,
          price: Number((ticketPrice * discountRate).toFixed(2)),
          currency: 'USD',
          quantity: selectedQuantity,
          product_id: `group-tickets-${theatricalSlug}`,
          sku: `group-tickets-${theatricalSlug}`,
          url: '',
          projectSlug: theatricalSlug,
          value: selectedQuantity * ticketPrice * discountRate,
        })
      }}
    >
      <Text className="photon-heading-sm md:photon-heading-md block text-center" color="core-gray-950" weight="bold">
        {t('flexGroupTickets', 'Flex Group Tickets')}
      </Text>
      {/* Limited Time Offer Banner */}
      <div className="my-2 rounded-md bg-oxide-bright px-3 py-2 text-center">
        <Text className="photon-caption-lg font-bold text-black">Limited • While Supplies Last</Text>
      </div>
      <Text
        color="core-gray-700"
        weight="medium"
        className="photon-paragraph-sm md:photon-paragraph-md mt-1 block pb-4 text-center"
      >
        <Translate t={t} i18nKey="youCanUseTheseTicketsOnAnySoundOfFreedomV6">
          Tickets only available at Angel Partner Theaters. Ticket prices estimated at $15. Additional charges may
          apply.{' '}
          <Button
            onClick={() => setModalType('faq')}
            className="inline font-semibold text-core-gray-950 underline"
            variant="plain"
          >
            Learn More
          </Button>
        </Translate>
      </Text>
      <div className="mb-6 mt-2 flex justify-center">
        <form className="flex w-full flex-col gap-2" onSubmit={(e) => e.preventDefault()}>
          {groupTicketProductVariantId &&
            OPTIONS.map((quantity) => {
              const title = t('groupTicketTitle', '{{quantity}} Tickets', { quantity })
              const discountLabel = getDiscountLabel(quantity)
              const isSelected = selectedQuantity === quantity
              const discountPercentage = calculateDiscountPercentage(getDiscountRate(quantity))
              const isHigherDiscount = discountPercentage === maxDiscountPercentage
              return (
                <TicketOptionDiscount
                  key={quantity}
                  onClick={() => {
                    setSelectedQuantity(quantity)
                  }}
                  title={title}
                  discountLabel={discountLabel}
                  ticketPrice={ticketPrice}
                  ticketQuantity={quantity}
                  discountRate={getDiscountRate(quantity)}
                  selected={isSelected}
                  specialTag={null}
                  isHigherDiscount={isHigherDiscount}
                />
              )
            })}
        </form>
      </div>

      <FlexColumn className="items-center gap-4">
        {shopifyCheckoutUrl ? (
          <>
            <LinkButton
              external
              href={shopifyCheckoutUrl}
              onClick={() => handleCtaClick(selectedQuantity)}
              className="bg-core-red-700 mx-auto flex w-full max-w-[380px] items-center justify-center gap-1 rounded-[14px] px-7 py-[17px] md:w-fit"
              variant="black"
              disabled={isProcessing}
            >
              {isProcessing && <LoadingSpinnerIcon color="black" size={16} />}
              {t('checkoutGroupTickets', 'Checkout')}
            </LinkButton>
            <Text className="photon-caption-md text-center text-core-gray-600">
              100% Satisfaction Guarantee • Secure Checkout
            </Text>
          </>
        ) : (
          <LoadingSpinnerIcon size={32} color="black" />
        )}
      </FlexColumn>
    </InView>
  )
}
